import {
	useState,
	useContext,
	Fragment,
	RefObject
} from 'react';
import {
	H3,
	H4,
	Caption,
	Text,
	Spacer,
	Tile,
	InfoChip,
	ErrorChip,
	WarnChip,
	Button,
	ManualCarrierCost,
	ModifierCheckbox,
	PriceAcceptance,
	ShipperClaimRaisedModal,
	toast,
} from '@components';
import { EditShipment } from '@state';
import { useRecoilValue } from 'recoil';
import ArrowUpIcon from '@mui/icons-material/NorthEast';
import ArrowDownIcon from '@mui/icons-material/SouthEast';
import {
	CHIP_SIZE,
	LoadingOptions,
	TPagedDialogContext,
	TShipmentOrder,
	TRaisedClaim,
	TShipmentAddress
} from '@types';
import { hasLocationManagedModifier, groupCargosByStop } from '@utils';
import { useValidation, useModifierForm } from '@hooks';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { EditShipmentContext } from '../EditShipment.Context';
import styles from '../EditShipment.module.scss';

const NO_PRICE_REASON = 'No price found for this modifier. Update the rate card to proceed.';

type TProps = {
	pagesRef?: RefObject<HTMLElement>;
	context?: TPagedDialogContext;
	refetch?: () => void;
}

export const Modifiers = (props: TProps) => {
	const { context, pagesRef, refetch } = props;
	const ctx = useContext(EditShipmentContext);
	const shipment = useRecoilValue(EditShipment);
	const pickupGroups = groupCargosByStop(shipment?.cargos ?? []).pickups;

	const [generalError, setGeneralError] = useState<string | null>(null);
	const [raisedClaims, setRaisedClaims] = useState<TRaisedClaim[]>([]);
	const navigate = useNavigate();

	/* Only the admin shipment page carries the Issues and Claims tab the claims are read on, and the dialog
	   sits over it either way, so it has to go. */
	const viewClaims = () => {
		setRaisedClaims([]);
		ctx?.closeDialog();
		navigate(`/transport/all-loads/${shipment?.id}?page=iandc`);
	};

	const {
		updateValidationErrors,
		connect,
		clearErrors
	} = useValidation({
		pallet_jack: z.boolean(),
		use_user_input_cost: z.boolean(),
		user_manually_cost: z.union([z.string(), z.null()]),
		user_manually_fuel_levy: z.union([z.string(), z.null()]),
		use_global_fuel_levy: z.boolean()
	});

	const dealWithErrors = (errPayload: { errors?: { [key: string]: string[] }; message: string }) => {
		if (errPayload.errors) {
			Object.entries(errPayload.errors).forEach(([key, value]) => {
				updateValidationErrors({
					field: key,
					issues: (value as string[]),
					path: key
				});
			});
		} else {
			setGeneralError(errPayload.message);
		}
	};

	const scrollPagesTo = (top: number) => {
		if (context && pagesRef && pagesRef.current) {
			setTimeout(() => {
				pagesRef && pagesRef.current && pagesRef.current.scrollTo({
					top,
					behavior: 'smooth'
				});
			}, 0);
		}
	};

	const {
		form,
		getStop,
		setPalletJack,
		setStopModifier,
		updateForm,
		isAllocated,
		canSubmit,
		recalculated,
		newShipperPrice,
		newCarrierCost,
		newCarrierManualCost,
		oldShipperPrice,
		oldCarrierCost,
		loadingModifierAdjustment,
		editWindow,
		submitModifiers,
	} = useModifierForm(shipment, {
		onFormChange: () => ctx?.setUpdateCargoSubmission?.(true),
		onRecalcSuccess: () => {
			setGeneralError(null);
			scrollPagesTo(pagesRef?.current?.scrollHeight ?? 0);
		},
		onRecalcError: dealWithErrors,
	});

	const modifiersAccess = ctx?.access.pages.modifiers;
	const formDisabled = modifiersAccess?.fields.modifierForm.disabled ?? false;

	/* Pallet jack is stored on the shipment, so any managed stop makes it managed. */
	const palletJackFromLocation = (shipment?.cargos ?? []).some((cargo) =>
		hasLocationManagedModifier(cargo.pickup, LoadingOptions.PALLET_JACK)
			|| hasLocationManagedModifier(cargo.dropoff, LoadingOptions.PALLET_JACK)
	);

	const updateModifiers = () => {
		submitModifiers({
			onSuccess: (claims) => {
				/* An edit past the shipper's invoice charges the difference on rather than moving the price,
				   so say so outright instead of leaving a plain "updated" toast to imply otherwise. */
				setRaisedClaims(claims);
				if (!claims.length) toast.success('Modifiers successfully updated');
				setGeneralError(null);
				clearErrors();
				if (refetch) refetch();
				scrollPagesTo(0);
			},
			onError: dealWithErrors,
		});
	};

	const generateStopTile = (stop: TShipmentAddress, dest: 'pickup' | 'dropoff', stopNumber: number) => {
		const stopForm = getStop(stop.id);
		return (
			<Tile stretch>
				<div className={`${styles.row} ${styles.spaceBetween}`}>
					<Caption>Stop {stopNumber}</Caption>
					<InfoChip text={dest === 'pickup' ? 'Pick-up' : 'Drop-off'} CustomIcon={dest === 'pickup' ? ArrowUpIcon : ArrowDownIcon} size={CHIP_SIZE.SMALL} />
				</div>
				<H4>{stop.business_name}</H4>
				<Text>{stop.address}</Text>
				<Spacer size={16} />
				<ModifierCheckbox
					label={'Tailgate'}
					checked={stopForm?.tailgate ?? false}
					onChange={(checked) => setStopModifier(stop.id, 'tailgate', checked)}
					fromLocation={hasLocationManagedModifier(stop, LoadingOptions.TAIL_GATE)}
					isAdmin
					disabled={formDisabled}
				/>
				{/* Hand unload is only priced on a drop-off. */}
				{dest === 'dropoff' && <>
					<Spacer size={8} />
					<ModifierCheckbox
						label={'Hand unload'}
						checked={stopForm?.hand_unload ?? false}
						onChange={(checked) => setStopModifier(stop.id, 'hand_unload', checked)}
						fromLocation={hasLocationManagedModifier(stop, LoadingOptions.HAND_LOAD)}
						isAdmin
						disabled={formDisabled}
					/>
				</>}
			</Tile>
		);
	};

	return (
		<>
			<H3>Update Modifiers</H3>
			<Spacer size={16} />
			<Text>Choose the required equipment for this shipment.</Text>
			<Spacer size={24} />
			{generalError && <>
				<ErrorChip text={generalError} stretch />
				<Spacer size={16} />
			</>}
			{modifiersAccess?.fields.invoicedWarning.visible && <>
				<WarnChip text={modifiersAccess.fields.invoicedWarning.reason ?? ''} stretch />
				<Spacer size={16} />
			</>}
			{modifiersAccess?.fields.superdockWarning.visible && <>
				<WarnChip text={modifiersAccess.fields.superdockWarning.reason ?? ''} stretch />
				<Spacer size={16} />
			</>}
			{modifiersAccess?.fields.modifierForm.visible && <>
				<Tile stretch>
					<H4>Equipment</H4>
					<Spacer size={16} />
					<ModifierCheckbox
						label={'Pallet jack'}
						checked={form.pallet_jack}
						onChange={setPalletJack}
						fromLocation={palletJackFromLocation}
						isAdmin
						disabled={formDisabled}
					/>
				</Tile>
				<Spacer size={16} />
				{/* Cargos collected from the same place are one stop — and these tiles already read and
					write by stop id, so a second tile was only ever a duplicate view of the same state. */}
				{pickupGroups.map((group, groupIndex) => {
					const cargo = (shipment as TShipmentOrder).cargos[group[0]];
					return <Fragment key={groupIndex}>
						{generateStopTile(cargo.pickup, 'pickup', (cargo.pick_position ?? groupIndex) + 1)}
						<Spacer size={16} />
					</Fragment>
				})}
				{!!shipment?.cargos.length && <>
					{generateStopTile(
						shipment.cargos[shipment.cargos.length - 1].dropoff,
						'dropoff',
						(shipment.cargos[shipment.cargos.length - 1].drop_position ?? pickupGroups.length) + 1,
					)}
					<Spacer size={16} />
				</>}
				<PriceAcceptance
					recalculated={recalculated}
					newShipperPrice={newShipperPrice}
					isAllocated={isAllocated}
					newCarrierCost={newCarrierCost}
					oldShipperPrice={oldShipperPrice}
					oldCarrierCost={oldCarrierCost}
					noPriceReason={NO_PRICE_REASON}
					editWindow={editWindow}
				/>
				{recalculated && isAllocated && newCarrierCost === 0 && (
					<ManualCarrierCost
						useUserInputCost={form.use_user_input_cost}
						userManuallyCost={form.user_manually_cost}
						userManuallyFuelLevy={form.user_manually_fuel_levy}
						useGlobalFuelLevy={form.use_global_fuel_levy}
						updateForm={updateForm}
						newCarrierManualCost={newCarrierManualCost}
						connect={connect}
					/>
				)}
				<Spacer size={16} />
				<div className={styles.btns}>
					<Button
						text={'Update Modifiers'}
						onClick={updateModifiers}
						disabled={(modifiersAccess?.fields.updateButton.disabled ?? false) || !canSubmit}
						loading={loadingModifierAdjustment}
					/>
				</div>
			</>}
			<ShipperClaimRaisedModal
				claims={raisedClaims}
				onClose={() => setRaisedClaims([])}
				onViewClaims={ctx?.access.userType === 'admin' ? viewClaims : undefined}
			/>
		</>
	);
};
